import React, {useState} from "react";
import { Dropdown, message, Modal, Tooltip } from "antd";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import TabBar from "../../components/navigation/TabBar";
import Breadcrumb from "../../components/navigation/Breadcrumb";
import SubmitButton from "../../components/buttons/SubmitButton";
import {
  CLONE_CAMPAIGN,
  DELETE_CAMPAIGN,
  RUN_CAMPAIGN_ACTION,
  isNativeDisplayCampaign,
} from "../../utils/api/campaign";
import { useMutation } from "@apollo/client";
import { Cog8ToothIcon } from "@heroicons/react/20/solid";
import {useCurrentUser} from '../../utils/CurrentUserContext';
import PublicationStatus, { STATUS_SIZES } from "../../components/badges/PublicationStatus";
import {Campaign as ICampaign, CampaignStatus, CampaignActionType, CampaignType, CompanyType} from '../../__generated__/graphql';
import dayjs from "dayjs";

const ACTION_BUTTON_TEXT = {
  [CampaignActionType.AcceptAllPublications]: 'Accept all',
  [CampaignActionType.Approve]: 'Approve',
  [CampaignActionType.Finish]: 'Finish',
  [CampaignActionType.Pause]: 'Pause',
  [CampaignActionType.Reject]: 'Reject',
  [CampaignActionType.RejectAllPublications]: 'Reject all',
  [CampaignActionType.Resume]: 'Resume',
  [CampaignActionType.SubmitToAdvertiser]: 'Submit to Advertiser',
  [CampaignActionType.SubmitToWebsites]: 'Submit to Websites',
  [CampaignActionType.ToDraft]: 'To draft',
}

const confirmAction = (actionCallback: () => void, actionType: CampaignActionType) => {
  const shouldConfirm = [CampaignActionType.Finish, CampaignActionType.Reject, CampaignActionType.ToDraft]

  if (shouldConfirm.includes(actionType)) {
    const finishContent = "The campaign and all its publications will be finished and they can't be started again."
    const defaultContent = "The campaign and all its publications will be moved to draft."

    return Modal.confirm({
      title: 'Are you sure?',
      content: actionType === CampaignActionType.Finish ? finishContent : defaultContent,
      okText: 'Ok',
      onOk: actionCallback
    })
  } else {
    actionCallback()
  }
}

const Campaign = () => {
  const {user} = useCurrentUser()
  const { campaign, marketplace } = useOutletContext<{campaign: ICampaign, marketplace: boolean}>();

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    update(cache) {
      cache.evict({ id: cache.identify({ __typename: 'Campaign', _id: campaign._id }) })
      cache.gc()
    }
  })

  const [cloneCampaign] = useMutation(CLONE_CAMPAIGN, {
    update(cache) {
      cache.evict({ fieldName: 'CampaignMany' })
      cache.gc()
    }
  })
  const [runCampaignAction] = useMutation(RUN_CAMPAIGN_ACTION)
  const [loadingCampaignAction, setLoadingCampaignAction] = useState<CampaignActionType>();
  const navigate = useNavigate()

  const campaignOwner = campaign.ownerCompanyId === user.company?._id
  const isNativeDisplay = isNativeDisplayCampaign(campaign.type as CampaignType)

  // Check if start date is in the past
  const isStartDateInPast = campaign.startDate && dayjs(campaign.startDate).isBefore(dayjs(), 'minute');
  const hasEverBeenActivated = !!campaign.everActivated;
  const submitActions = [CampaignActionType.SubmitToWebsites, CampaignActionType.SubmitToAdvertiser];

  const tabs = campaignOwner
  ? [
    { name: "Details", to: "details" },
    { name: "Websites", to: "websites" },
    ...(isNativeDisplay ? [] : [{ name: "Content", to: "meta-article" }]),
    { name: "Teasers", to: "teasers" },
    { name: "Report", to: "report" },
  ]
  : [
    { name: "Websites", to: "websites" },
    ...(user.company.type === CompanyType.Publisher? [{ name: "Teasers", to: "teasers" }] : []),
    ...(campaign.type !== CampaignType.Filler && !marketplace ? [{ name: "Report", to: "report" }] : []),
  ];

  const pages = [
    marketplace ? { name: "Marketplace", href: "/marketplace-campaigns", current: false } : { name: "Campaigns", href: "/campaigns", current: false },
    {
      name: campaign?.name ? campaign.name : "...",
      href: `/${marketplace ? 'marketplace-campaigns' : 'campaigns'}/${campaign?._id ? campaign._id : ""}/${tabs[0].to}`,
      current: true,
    },
  ];
  const status = campaign.status;
  // const status = CampaignStatus.ACTIVE as CampaignStatus
  const chatChannel = `chat:campaign:${campaign._id}`;
  const canDeleteCampaign = campaign.status === CampaignStatus.Draft
  const menuProps = {
    items: [
      {
        label: 'Duplicate',
        key: '1',
      },
      {
        label: 'Delete',
        key: '2',
        disabled: !canDeleteCampaign,
        danger: true,
        title: canDeleteCampaign ? '' : 'Only Draft campaigns can be deleted'
      },
      
    ],
    onClick: ({key}: {key: string}) => {
      if (key === '1') {
        message.loading({
          content: 'Duplicating campaign',
          duration: 0
        })
        return cloneCampaign({variables: {campaignId: campaign._id},
          onCompleted: (data) => {
            message.destroy()
            message.success('Campaign duplicated', 1.5)
            navigate(`/campaigns/${data.CampaignClone._id}/details`)
          }
        })
      }
      Modal.confirm({
        title: 'Are you sure?',
        content: 'The campaign will be deleted together with all the publications and teasers.',
        okText: 'Delete',
        okType: 'danger',
        onOk() {
          return new Promise<void>((resolve) => {
            deleteCampaign({ variables: { id: campaign._id },
              onCompleted: () => {
                navigate('/campaigns')
                resolve()
              }})
            })
        },
      });
    }
  }
  return (
    <main>
      <div className="text-gray-900 flex w-full justify-between bg-white rounded-lg mb-4">
        <Breadcrumb pages={pages} />
        <div className="flex p-2">
          <PublicationStatus status={status as CampaignStatus} size={STATUS_SIZES.LARGE} />
          
          {campaign.actions?.map(({ action, disabled, disabledReason }) => {
            // Disable submit when start date is past only if the campaign has never been activated
            const isSubmitAction = submitActions.includes(action);
            const shouldDisableDueToStartDate = campaignOwner && isSubmitAction && !hasEverBeenActivated && isStartDateInPast;
            const finalDisabled = disabled || shouldDisableDueToStartDate;
            const finalDisabledReason = shouldDisableDueToStartDate
              ? 'Start date cannot be in the past'
              : disabledReason;

            return (
              <Tooltip placement="bottom" title={finalDisabledReason} key={action}>
                <div className="inline-flex">
                  <SubmitButton
                    className="ml-8"
                    submitText={ACTION_BUTTON_TEXT[action]}
                    loading={loadingCampaignAction === action}
                    disabled={finalDisabled}
                    onClick={() => confirmAction(async () => {
                      setLoadingCampaignAction(action)

                      runCampaignAction({ variables: { action, campaignId: campaign._id }})
                      .catch((error) => {
                        message.error(error.message.replace(new RegExp(Object.values(CampaignActionType).join('|')), ''))
                        throw new Error('TODO: Disable campaign action buttons to prevent errors')
                      })
                      .finally(() => {
                        setLoadingCampaignAction(undefined)
                      })
                  }, action)}
                  />
                </div>
              </Tooltip>
            )
          })}

          {!marketplace && (
            <Dropdown menu={menuProps} placement="bottomRight">
              <Cog8ToothIcon className="text-gray-400 group-hover:text-gray-500 m-[8px] h-5 w-5"/>
            </Dropdown>
          )}
        </div>
      </div>
      <TabBar className="mt-4" tabs={tabs} />
      {/* CampaignOwner: {campaignOwner.toString()} */}

      <section className="mt-8 lg:grid lg:grid-cols-10 lg:gap-8">
        <div className="lg:col-span-10">
          <Outlet context={{ campaign, marketplace }} />
        </div>
        {/*<div className="mt-8 lg:mt-0 lg:col-span-3">
          <div className="sticky top-4 space-y-4">
            <section
              aria-labelledby="chat-title"
              className="lg:col-span-1 lg:col-start-3"
            >
                <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6 divide-y">
                <ChatColumn currentChannel={chatChannel} show={true} />
              </div>
            </section>
          </div>
        </div>*/}
      </section>
    </main>
  );
};

export default Campaign;
